import React, { useEffect, useState } from 'react';
import axios from 'axios';
import i18n from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ImageUploadEdit from './ImageUploadEdit';
import i18next from 'i18next';
import ProductAttributesSelector from './ProductAttributesSelector';
import MessageAlerts from './MessageAlerts';

export default function UpdateCard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const localhost = process.env.REACT_APP_LOCALHOST;
  const localimage = process.env.REACT_APP_LOCALHOST_IMG;
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setloading] = useState(true);
  const [nameEn, setNameEn] = useState('');
  const [nameFr, setNameFr] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionFr, setDescriptionFr] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [forValue, setForValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [allOptions, setAllOptions] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [errorMessages, setErrorMessages] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [originalImages, setOriginalImages] = useState([]);

  const [colors, setColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState([]);
  const [allColors, setAllColors] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productRes = await axios.get(`${localhost}get-product/${slug}`);
        const prod = productRes.data.product;
        const images = prod.imgs ? JSON.parse(prod.imgs) : [];
        setOriginalImages(images);

        if (images.length > 0) {
          setMainImage(`${localimage}${images[0]}`);
          setAdditionalImages(images.slice(1).map(img => `${localimage}${img}`));
        }
        setNameEn(prod.name.en);
        setNameFr(prod.name.fr);
        setDescriptionEn(prod.description.en);
        setDescriptionFr(prod.description.fr);
        setPrice(prod.price);
        setStock(prod.stock);
        setCategoryId(prod.category_id);
        setForValue(prod.gender);

        const categoriesRes = await axios.get(`${localhost}all-categories`);
        setCategories(categoriesRes.data);

        // sizes :
        const sizesRes = await axios.get(`${localhost}all-sizes`);
        const allSizeOptions = sizesRes.data.map((item) => ({ value: item.id, label: item.name }));
        setSizes(sizesRes.data);
        const selectedSizes = prod.sizes.map((size) => ({ value: size.size_id, label: size.name }));
        setAllOptions([{ value: "select_all", label: "Select All" }, ...allSizeOptions]);
        setSelectedOptions(selectedSizes);

        // colors :
        const colorsRes = await axios.get(`${localhost}all-colors`);
        const allColorOptions = colorsRes.data.map((item) => ({ value: item.id, label: item.name[i18next.language] }));
        setColors(colorsRes.data);
        const selectedColors = prod.colors.map((color) => ({ value: color.color_id, label: color.name[i18next.language] }));
        setAllColors([{ value: "select_all", label: "Select All" }, ...allColorOptions]);
        setSelectedColor(selectedColors);
      } catch (err) {
        // 
      } finally {
        setloading(false);
      }
    };
    fetchData();
  }, [localhost, slug, localimage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();

    form.append('name[en]', nameEn);
    form.append('name[fr]', nameFr);
    form.append('description[en]', descriptionEn);
    form.append('description[fr]', descriptionFr);
    form.append('price', price);
    form.append('stock', stock);
    form.append('category_id', categoryId);
    form.append('gender', forValue);
    selectedOptions.forEach((option) => form.append('sizes[]', option.value));
    selectedColor.forEach((option) => form.append('colors[]', option.value));

    const existingMainImage = typeof mainImage === 'string' ? mainImage.replace(localimage, '') : null;
    const existingAdditionalImages = additionalImages
      .filter(img => typeof img === 'string')
      .map(img => img.replace(localimage, ''));

    if (existingMainImage) {
      form.append('existing_imgs[]', existingMainImage);
    }
    existingAdditionalImages.forEach(img => {
      form.append('existing_imgs[]', img);
    });

    if (typeof mainImage === 'object') {
      form.append('main_image', mainImage);
    }

    additionalImages.forEach((img, index) => {
      if (typeof img === 'object') {
        form.append('additional_images[]', img);
      }
    });

    try {
      const res = await axios.post(`${localhost}admin/update-product/${slug}`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      setSuccessMessage('Product successfully updated!');
      setTimeout(() => navigate("/admin/products"), 1000);

      await axios.put(`${localhost}admin/update-stock-combinations/${res.data}`, { pre_stock: stock }, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => {
          setTimeout(() => {
            navigate(`/admin/stock-management/${res.data}`);
          }, 1000);
        }).catch((err) => {
          // console.error(err);
          setSuccessMessage('');
        });
    } catch (err) {
      console.error(err);
      const errJson = err ? JSON.parse(err.request?.response) : null;
      if (errJson && errJson.error) {
        setErrorMessages(errJson.error);
      } else {
        setErrorMessages({ general: ['An unexpected error occurred.'] });
      }
      setSuccessMessage('');
    }
  };

  if (loading) return <div className="min-h-[55vh] flex items-center justify-center"><div className="loading-spinner" /></div>;

  return (
    <div className="antialiased">
      <div className="max-w-7xl mx-auto">
        <div className="w-full flex justify-start mb-2">
          <a href="/admin/products" className="capitalize flex flex-wrap items-center gap-1 bg-black hover:bg-black/80 text-white px-2 py-1.5 rounded-md">
            <ArrowLeftIcon className="size-4" />back
          </a>
        </div>
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
          <form onSubmit={handleSubmit} className="px-4 py-6 sm:p-8">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold leading-7 text-gray-900">Update Product</h2>
                <p className="mt-1 text-sm leading-6 text-gray-600">Modify the product details below.</p>
              </div>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">English Name</label>
                  <input type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">French Name</label>
                  <input type="text" value={nameFr} onChange={(e) => setNameFr(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">English Description</label>
                  <textarea value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} rows={3} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">French Description</label>
                  <textarea value={descriptionFr} onChange={(e) => setDescriptionFr(e.target.value)} rows={3} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm" />
                </div>
              </div>
              <div className="w-full">
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price</label>
                    <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stock</label>
                    <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 mt-9">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <select value={forValue} onChange={(e) => setForValue(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm">
                      <option value="">Select Gender</option>
                      <option value="all">All</option>
                      <option value="men">Men</option>
                      <option value="women">Women</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500 sm:text-sm">
                      <option value="">Select Category</option>
                      {categories.map((item, index) => (
                        <option key={index} value={item.id}>{item.name[i18n.language]}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-9">
                  <ProductAttributesSelector
                    sizes={sizes}
                    colors={colors}
                    selectedSizes={selectedOptions}
                    selectedColors={selectedColor}
                    onSizesChange={setSelectedOptions}
                    onColorsChange={setSelectedColor}
                  />
                </div>
              </div>

              <ImageUploadEdit
                mainImage={mainImage}
                additionalImages={additionalImages}
                onMainImageChange={setMainImage}
                onAdditionalImagesChange={setAdditionalImages}
              />

              <MessageAlerts
                successMessage={successMessage}
                errorMessages={errorMessages}
              />

              <div className="mt-6 flex items-center justify-end gap-x-6">
                <button type="submit" className="rounded-md bg-black hover:bg-black/80 px-6 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600">
                  Update Product
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}